import { useState } from "react";
import { Paper, Chip, Box, Typography, Link as MuiLink } from "@mui/material";

export default function EventCard({ ev, index, gradient }) {
    const [hoverStyle, setHoverStyle] = useState({});

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const intensity = 22;

        setHoverStyle({
            transform: "translateY(-6px)",
            boxShadow: `${x / 18}px ${y / 18}px ${intensity}px rgba(0,0,0,0.25)`,
        });
    };

    const resetHover = () => setHoverStyle({});

    return (
        <Paper
            onMouseMove={handleMouseMove}
            onMouseLeave={resetHover}
            sx={{
                ...hoverStyle,
                transition: "all 0.25s ease",
                cursor: "default",
                p: { xs: 1.5, sm: 3 },
                border: "1px solid #e5e5e5",
                borderRadius: 2,
                height: { xs: 230, sm: 260 },
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 1,

                opacity: 0,
                transform: "translateY(20px)",
                animation: "fadeInUp 0.6s ease forwards",
                animationDelay: `${index * 0.08}s`,

                "@keyframes fadeInUp": {
                    "0%": { opacity: 0, transform: "translateY(20px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" },
                },
            }}
        >
            {/* 🏷️ Badge sport — élément normal (pas de position absolue), aligné
                à droite via flexbox. Ne peut jamais déborder de la carte :
                contrairement à un positionnement absolu, flexbox respecte
                toujours la largeur du parent, sans calcul de maxWidth à deviner. */}
            {ev.sport && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                    <Chip
                        label={ev.sport.toUpperCase()}
                        size="small"
                        sx={{
                            maxWidth: "100%",
                            height: "auto",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            paddingX: 0.75,
                            paddingY: 0.25,
                            background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                            backgroundSize: "200% 200%",
                            animation: "gradientMove 4s ease infinite",
                            borderRadius: "6px",
                            boxShadow: `0 0 12px ${gradient.glow}`,
                            "& .MuiChip-label": {
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingLeft: 4,
                                paddingRight: 4,
                            },

                            "@keyframes gradientMove": {
                                "0%": { backgroundPosition: "0% 50%" },
                                "50%": { backgroundPosition: "100% 50%" },
                                "100%": { backgroundPosition: "0% 50%" },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Ligne texte / logo, sous le badge */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "row", gap: { xs: 1, sm: 2 } }}>
                {/* Contenu texte : titre/date/liens */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                            {ev.nom}
                        </Typography>
                        <Typography sx={{ color: "text.secondary", mt: 1, mb: 2, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                            {ev.date} — {ev.lieu}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {ev.site_officiel && (
                            <MuiLink href={ev.site_officiel} target="_blank">
                                🔗 Site officiel
                            </MuiLink>
                        )}
                        {ev.startlist && (
                            <MuiLink href={ev.startlist} target="_blank">
                                📄 Liste de départ
                            </MuiLink>
                        )}
                        {ev.resultats && (
                            <MuiLink href={ev.resultats} target="_blank">
                                🏁 Résultats
                            </MuiLink>
                        )}
                        {ev.livetiming && (
                            <MuiLink href={ev.livetiming} target="_blank" color="secondary.main">
                                ⏱️ LiveTiming
                            </MuiLink>
                        )}
                    </Box>
                </Box>

                {/* 🖼️ Illustration optionnelle — absente si ev.image n'est pas défini.
                    Le logo garde ses proportions naturelles : on contraint sa
                    largeur ET sa hauteur max, et c'est la dimension la plus
                    limitante qui détermine sa taille réelle affichée — jamais
                    ni rogné, ni déformé. */}
                {ev.image && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            alignSelf: "flex-start",
                            maxWidth: { xs: 90, sm: 110 },
                            maxHeight: { xs: 130, sm: 150 },
                        }}
                    >
                        <Box
                            component="img"
                            src={ev.image}
                            alt=""
                            sx={{
                                display: "block",
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Paper>
    );
}